import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { currentCountSessionQuery } from './use-count-session'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * The rule for every booking or manual correction of stock (delivery booking, Wareneingang, set
 * on-hand, movements): while the store has an open count, the user confirms once more before the
 * booking runs, because a booking during a count can skew it. Warns only, never blocks — when the
 * check itself fails, the booking runs as if no count were open.
 *
 * Wrap the booking in `guard(...)`, render `dialog` next to the caller's own dialog, and disable
 * the submit button while `checking`.
 */
export function useCountSessionGuard(restaurantId: number | null) {
  const { t } = useTranslation('inventory-count')
  const queryClient = useQueryClient()
  const [checking, setChecking] = useState(false)
  const [pending, setPending] = useState<{
    actor: string
    run: () => void
  } | null>(null)

  const guard = async (run: () => void) => {
    if (restaurantId == null) return run()
    setChecking(true)
    let actor: string | null = null
    try {
      const current = await queryClient.fetchQuery(
        currentCountSessionQuery(restaurantId),
      )
      actor = current?.session.startedBy ?? null
    } catch {
      // Warn-only: an unreadable session must not stop the booking.
    } finally {
      setChecking(false)
    }
    if (actor == null) run()
    else setPending({ actor, run })
  }

  const dialog = (
    <AlertDialog
      open={pending != null}
      onOpenChange={(open) => {
        if (!open) setPending(null)
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <TriangleAlert className="h-5 w-5 shrink-0 text-warning" />
            {t('activeWarning.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('activeWarning.by', { actor: pending?.actor })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('activeWarning.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              pending?.run()
              setPending(null)
            }}
          >
            {t('activeWarning.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return { guard, checking, dialog }
}
